import { useMemo, useRef, useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useCan, Can } from '../contexts/PermissionsContext'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { AppIcon } from './ui/icon'
import { PIIBadge } from './PIIBadge'
import type { PIIStatus } from '../lib/pii'
import { mdiAccount, mdiArchiveOutline, mdiAccountGroup, mdiArrowLeft, mdiCalendar, mdiCheck, mdiClose, mdiGraphOutline, mdiMapMarker, mdiPencil, mdiScaleBalance, mdiScriptTextOutline, mdiShieldAlertOutline, mdiSourceBranch, mdiTrashCanOutline } from '@mdi/js'
import { useContract, useContractStakeholderTypes, useContractTypes, useUpdateContract, useDeleteContract, useAddContractStakeholder, useRemoveContractStakeholder, useAddContractAsset, useRemoveContractAsset } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { getLocalizedName } from '../lib/localization'
import { NullableDateField } from './ui/NullableDateField'
import { EntityIssuesList } from './issues/EntityIssuesList'
import { EntityTagsSection } from './tags/EntityTagsSection'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog'
import { Alert, AlertDescription } from './ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Textarea } from './ui/textarea'
import { EditableValue } from './ui/editable-value'
import { ContractClausesTab } from './ContractClausesTab'
import { LeaseTermsCard } from './LeaseTermsCard'
import { RentScheduleTab } from './lease/RentScheduleTab'
import { GuaranteesCard } from './lease/GuaranteesCard'
import { GuaranteeConflictConfirmDialog } from './lease/GuaranteeConflictConfirmDialog'
import { guaranteeConflicts, type GuaranteeConflict } from '../api/leaseRent'
import { ContractLeaseExitCard } from './ContractLeaseExitCard'
import { PrimaryAssetRadio, PrimaryMissingBanner } from './ContractPrimaryAsset'
import { ContractImprovementTab } from './ContractImprovementTab'
import { ContractRelatedExpenses } from './ContractRelatedExpenses'
import { ContractDocuments } from './ContractDocuments'
import { ContractReportsTab } from './ContractReportsTab'
import { ContractExecutionCard } from './ContractExecutionCard'
import { CommercialLeaseCard } from './leases/CommercialLeaseCard'
import { ContractChainStrip } from './ContractChainStrip'
import { ContractCoverageCard } from './assemblies/ContractCoverageCard'
import { SignatureRequestsPanel } from './signatures/SignatureRequestsPanel'
import { groupContractTypes, contractTypeLabel } from '../lib/contract-type-tree'
import { CONTRACT_STATUS_OPTIONS } from '../lib/contract-status'
import { useContractQualityIssues } from '../hooks/useContractQualityIssues'
import { useTicketSubtypes, subtypeLabel } from '../hooks/useTicketSubtypes'
import { useUrlTab } from '../hooks/useUrlTab'
import { EmptyState } from './ui/empty-state'
import { UserSelect } from './UserSelect'
import { AssetPicker } from './AssetPicker'
import { AssetName } from './ArchivedMarker'
import { SECTION_ICONS } from '../lib/section-icons'

export function ContractPage() {
  const { org, contractUid } = useParams({ from: '/$org/contracts/$contractUid' })
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()
  const canWrite = useCan('contracts.write')

  // Open contract-quality tickets targeting this contract (domain `contract`,
  // spec 2026-07-31-01) — drives the "À fiabiliser" header badge; the tickets
  // themselves are listed in the issues section below.
  const { byContract: qualityByContract } = useContractQualityIssues(org || 'demo')
  const { byCode: subtypeCatalog } = useTicketSubtypes(org || 'demo')
  const qualityIssues = qualityByContract[contractUid] || []

  const { data: contract, isLoading, error } = useContract(
    org || 'demo',
    contractUid || '',
    ['contract_type', 'stakeholders', 'user', 'assets']
  )

  // Live entity notifications: the detail scope refreshes the contract's own
  // row precisely; the collection scope is ALSO needed because
  // stakeholder/asset-link writes (contract_actors/contract_assets child
  // tables) only ever carry a collection-only hint — see the comment on the
  // `contracts` entry in live-invalidation.ts.
  useLiveSubscription({ entity: 'contracts', uid: contractUid })
  useLiveSubscription({ entity: 'contracts' })

  const { data: stakeholderTypes = [] } = useContractStakeholderTypes(org || 'demo')
  const { data: contractTypes = [] } = useContractTypes(org || 'demo')
  const contractTypeGroups = useMemo(() => groupContractTypes(contractTypes, locale), [contractTypes, locale])

  // The API embeds each stakeholder's user (via the `user` include), so there is
  // no client-side join to do. Resolving it against the /users list instead
  // silently broke past the first page: that endpoint defaults to 20 items, so
  // any stakeholder ranked lower rendered as "Unknown Stakeholder".
  const enrichedStakeholders = useMemo(() => contract?.stakeholders || [], [contract])
  const contractAssets = useMemo(() => contract?.assets || [], [contract])
  // A contract still linked to an archived asset: entity extraction skips it
  // (`filing_asset_not_found`), which used to fail silently.
  const archivedContractAssets = useMemo(
    () => contractAssets.filter((a: any) => a.asset?.archived_at),
    [contractAssets],
  )

  // Mutations for edit and delete
  const updateMutation = useUpdateContract(org || 'demo', contractUid || '')

  // PII verdict (spec 2026-08-13-02 §4). `clear` falls back to the contract
  // type's presumption rather than meaning "no PII".
  const setPIIStatus = (status: PIIStatus) => {
    void updateMutation.mutateAsync({ pii_status: status })
  }
  const deleteMutation = useDeleteContract(org || 'demo')

  // Stakeholder / asset link mutations (§4, §5, §6) — the four hooks already
  // invalidate the contract detail + list caches on success (useQueries.ts).
  const addStakeholderMutation = useAddContractStakeholder(org || 'demo', contractUid || '')
  const removeStakeholderMutation = useRemoveContractStakeholder(org || 'demo', contractUid || '')
  const addAssetMutation = useAddContractAsset(org || 'demo', contractUid || '')
  const removeAssetMutation = useRemoveContractAsset(org || 'demo', contractUid || '')

  // Every single-value field below is click-to-edit (spec 2026-10-11-29, the
  // "Editable value" rule of the design reference): the value is the click
  // target, a boolean `editingX` swaps in the editor, a draft value is seeded
  // from `contract`, Enter and blur save, Escape cancels, Ctrl/Cmd+Enter saves
  // the textarea, a select saves on pick. Sets of linked entities (tags,
  // stakeholders, assets) keep their card-level pencil / Done flow.

  // Enter and Escape already settled the edit: the blur that follows (the
  // editor unmounting) must not save a second time.
  const blurGuard = useRef(false)
  const blurCommit = (commit: () => void) => () => {
    if (blurGuard.current) {
      blurGuard.current = false
      return
    }
    commit()
  }

  // --- Title (§2) -----------------------------------------------------------
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState('')
  const [titleError, setTitleError] = useState('')

  const startTitleEdit = () => {
    blurGuard.current = false
    setTitleValue((contract as any)?.name || '')
    setTitleError('')
    setEditingTitle(true)
  }

  const cancelTitleEdit = () => {
    setTitleValue((contract as any)?.name || '')
    setTitleError('')
    setEditingTitle(false)
  }

  const commitTitle = () => {
    const trimmed = titleValue.trim()
    if (!trimmed) {
      setTitleError(t('detail.title_required'))
      return
    }
    if (trimmed === ((contract as any)?.name || '')) {
      setTitleError('')
      setEditingTitle(false)
      return
    }
    setTitleError('')
    updateMutation.mutate({ name: trimmed } as any, {
      onSuccess: () => setEditingTitle(false),
      onError: (err: any) => setTitleError(err?.title || err?.message || t('detail.failed_update_field'))
    })
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      blurGuard.current = true
      commitTitle()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      blurGuard.current = true
      cancelTitleEdit()
    }
  }

  // --- Status (§2) — Select value applied on Save, not on change -----------
  const [editingStatus, setEditingStatus] = useState(false)
  const [statusValue, setStatusValue] = useState('')
  const [statusError, setStatusError] = useState('')

  const statusPicked = useRef(false)
  const startStatusEdit = () => {
    statusPicked.current = false
    setStatusValue((contract as any)?.status || 'draft')
    setStatusError('')
    setEditingStatus(true)
  }

  const cancelStatusEdit = () => {
    setStatusError('')
    setEditingStatus(false)
  }

  const commitStatus = (next: string) => {
    const current = (contract as any)?.status || 'draft'
    if (next === current) {
      setStatusError('')
      setEditingStatus(false)
      return
    }
    setStatusError('')
    setStatusValue(next)
    statusPicked.current = true
    updateMutation.mutate({ status: next } as any, {
      onSuccess: () => setEditingStatus(false),
      onError: (err: any) => setStatusError(err?.title || err?.message || t('detail.failed_update_field'))
    })
  }

  // --- Contract type (§2) — retired from the old page-wide /edit mode ------
  const [editingType, setEditingType] = useState(false)
  const [typeValue, setTypeValue] = useState('')
  const [typeError, setTypeError] = useState('')

  const startTypeEdit = () => {
    setTypeValue((contract as any)?.contract_type?.uid || '')
    setTypeError('')
    setEditingType(true)
  }

  const cancelTypeEdit = () => {
    setTypeError('')
    setEditingType(false)
  }

  const commitType = (next: string) => {
    const current = (contract as any)?.contract_type?.uid || ''
    if (next === current) {
      setTypeError('')
      setEditingType(false)
      return
    }
    setTypeError('')
    setTypeValue(next)
    updateMutation.mutate({ contract_type_uid: next || undefined } as any, {
      onSuccess: () => setEditingType(false),
      onError: (err: any) => setTypeError(err?.title || err?.message || t('detail.failed_update_field'))
    })
  }

  // --- Reference / code (§2) — retired from the old page-wide /edit mode ---
  const [editingReference, setEditingReference] = useState(false)
  const [referenceValue, setReferenceValue] = useState('')
  const [referenceError, setReferenceError] = useState('')

  const startReferenceEdit = () => {
    blurGuard.current = false
    setReferenceValue((contract as any)?.code || '')
    setReferenceError('')
    setEditingReference(true)
  }

  const cancelReferenceEdit = () => {
    setReferenceError('')
    setEditingReference(false)
  }

  const commitReference = () => {
    const current = (contract as any)?.code || ''
    const trimmed = referenceValue.trim()
    if (trimmed === current) {
      setReferenceError('')
      setEditingReference(false)
      return
    }
    setReferenceError('')
    updateMutation.mutate({ code: trimmed || undefined } as any, {
      onSuccess: () => setEditingReference(false),
      onError: (err: any) => setReferenceError(err?.title || err?.message || t('detail.failed_update_field'))
    })
  }

  const handleReferenceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      blurGuard.current = true
      commitReference()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      blurGuard.current = true
      cancelReferenceEdit()
    }
  }

  // --- Description (§2) -----------------------------------------------------
  const [editingDescriptionField, setEditingDescriptionField] = useState(false)
  const [descriptionValue, setDescriptionValue] = useState('')
  const [descriptionError, setDescriptionError] = useState('')

  const startDescriptionEdit = () => {
    blurGuard.current = false
    setDescriptionValue((contract as any)?.description || '')
    setDescriptionError('')
    setEditingDescriptionField(true)
  }

  const cancelDescriptionEdit = () => {
    setDescriptionValue((contract as any)?.description || '')
    setDescriptionError('')
    setEditingDescriptionField(false)
  }

  const commitDescription = () => {
    const current = (contract as any)?.description || ''
    if (descriptionValue === current) {
      setDescriptionError('')
      setEditingDescriptionField(false)
      return
    }
    setDescriptionError('')
    updateMutation.mutate({ description: descriptionValue } as any, {
      onSuccess: () => setEditingDescriptionField(false),
      onError: (err: any) => setDescriptionError(err?.title || err?.message || t('detail.failed_update_field'))
    })
  }

  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      blurGuard.current = true
      commitDescription()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      blurGuard.current = true
      cancelDescriptionEdit()
    }
  }

  // --- Tags (§3) — EntityTagsSection's own picker/remove crosses gated -----

  // --- Stakeholders — add / remove (§3) -----------------------------------
  const [editingStakeholders, setEditingStakeholders] = useState(false)
  const [newStakeholderTypeUid, setNewStakeholderTypeUid] = useState('')
  const [newStakeholderUserUid, setNewStakeholderUserUid] = useState('')
  const [stakeholderAddError, setStakeholderAddError] = useState('')
  const [stakeholderRemoveErrors, setStakeholderRemoveErrors] = useState<Record<string, string>>({})

  const stakeholderAlreadyLinked = useMemo(() => {
    if (!newStakeholderUserUid || !newStakeholderTypeUid) return false
    return enrichedStakeholders.some((s: any) => s.user_uid === newStakeholderUserUid && s.stakeholder_type_uid === newStakeholderTypeUid)
  }, [enrichedStakeholders, newStakeholderUserUid, newStakeholderTypeUid])

  const resetStakeholderAddRow = () => {
    setNewStakeholderTypeUid('')
    setNewStakeholderUserUid('')
    setStakeholderAddError('')
  }

  // Pencil → edit mode: the add row is open by default for as long as the
  // card is in edit mode (§1.5) — there is no separate open/closed state for
  // it any more. "Cancel" on the add row only resets its own draft fields;
  // "Done" is what returns the whole card to display mode (§1.5, no Cancel
  // that reverts already-committed links — default from the spec's open
  // questions).
  const startStakeholdersEdit = () => {
    resetStakeholderAddRow()
    setEditingStakeholders(true)
  }

  const doneStakeholdersEdit = () => {
    resetStakeholderAddRow()
    setEditingStakeholders(false)
  }

  // A GUARANTOR the lease's guarantees exclude answers 409 guarantee_conflict:
  // the manager may record it anyway (spec 2026-10-25-08 step 9).
  const [stakeholderConflicts, setStakeholderConflicts] = useState<GuaranteeConflict[] | null>(null)
  const confirmAddStakeholder = (confirm = false) => {
    if (!newStakeholderTypeUid || !newStakeholderUserUid) return
    if (stakeholderAlreadyLinked) {
      setStakeholderAddError(t('detail.stakeholder_already_linked'))
      return
    }
    setStakeholderAddError('')
    addStakeholderMutation.mutate({ stakeholder_type_uid: newStakeholderTypeUid, user_uid: newStakeholderUserUid, confirm_guarantee_conflict: confirm }, {
      onSuccess: () => {
        setStakeholderConflicts(null)
        resetStakeholderAddRow()
      },
      onError: (err: any) => {
        const conflicts = guaranteeConflicts(err)
        if (conflicts) {
          setStakeholderConflicts(conflicts)
          return
        }
        setStakeholderAddError(err?.title || err?.message || t('detail.failed_update_field'))
      }
    })
  }

  const removeStakeholder = (stakeholderUid: string) => {
    setStakeholderRemoveErrors(prev => ({ ...prev, [stakeholderUid]: '' }))
    removeStakeholderMutation.mutate(stakeholderUid, {
      onError: (err: any) => setStakeholderRemoveErrors(prev => ({ ...prev, [stakeholderUid]: err?.title || err?.message || t('detail.failed_update_field') }))
    })
  }

  // --- Assets — add / remove (§3) -----------------------------------------
  const [editingAssets, setEditingAssets] = useState(false)
  const [newAssetUid, setNewAssetUid] = useState('')
  const [newAssetLabel, setNewAssetLabel] = useState('')
  const [assetAddError, setAssetAddError] = useState('')
  const [assetRemoveErrors, setAssetRemoveErrors] = useState<Record<string, string>>({})

  const assetAlreadyLinked = useMemo(() => {
    if (!newAssetUid) return false
    return contractAssets.some((a: any) => a.asset_uid === newAssetUid)
  }, [contractAssets, newAssetUid])

  const resetAssetAddRow = () => {
    setNewAssetUid('')
    setNewAssetLabel('')
    setAssetAddError('')
  }

  const startAssetsEdit = () => {
    resetAssetAddRow()
    setEditingAssets(true)
  }

  const doneAssetsEdit = () => {
    resetAssetAddRow()
    setEditingAssets(false)
  }

  const confirmAddAsset = () => {
    if (!newAssetUid) return
    if (assetAlreadyLinked) {
      setAssetAddError(t('detail.asset_already_linked'))
      return
    }
    setAssetAddError('')
    addAssetMutation.mutate({ asset_uid: newAssetUid }, {
      onSuccess: () => {
        resetAssetAddRow()
      },
      onError: (err: any) => setAssetAddError(err?.title || err?.message || t('detail.failed_update_field'))
    })
  }

  const removeAsset = (assetUid: string) => {
    setAssetRemoveErrors(prev => ({ ...prev, [assetUid]: '' }))
    removeAssetMutation.mutate(assetUid, {
      onError: (err: any) => setAssetRemoveErrors(prev => ({ ...prev, [assetUid]: err?.title || err?.message || t('detail.failed_update_field') }))
    })
  }

  // Detail body tabs (Details / Clauses) — lives in the URL (`?tab=`), mirroring
  // the asset detail page pattern (see useUrlTab) so a tab is bookmarkable/shareable
  // and browser back/forward moves between tabs.
  const [activeTab, setActiveTab] = useUrlTab(contractUid || '', 'details')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleTabChange = (tabId: string) => {
    const url = new URL(window.location.href)
    url.searchParams.set('tab', tabId)
    window.history.pushState({}, '', url.toString())
    setActiveTab(tabId)
  }

  const saveDateField = async (field: 'start_date' | 'end_date', next: string | null) => {
    await updateMutation.mutateAsync({ [field]: next } as any)
  }

  const confirmDelete = async () => {
    if (!contract) return
    try {
      await deleteMutation.mutateAsync(contract.uid)
      setShowDeleteDialog(false)
      window.location.href = `/${org}/contracts`
    } catch (error) {
      console.error('Failed to delete contract:', error)
    }
  }

  // Helper functions
  const getContractTitle = (contract: any) => {
    return contract?.name || contract?.code || t('list.untitled')
  }

  const getContractTypeName = (contract: any) => {
    if (!contract?.contract_type) return '-'
    if (contract.contract_type.names) {
      const names = contract.contract_type.names as Record<string, string>
      return names[locale] || names.en || names.fr || Object.values(names)[0] || contract.contract_type.code || '-'
    }
    return contract.contract_type.code || '-'
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'open': return 'default'
      case 'in_progress': return 'secondary'
      case 'closed': return 'destructive'
      default: return 'outline'
    }
  }

  const getStatusLabel = (status: string) => {
    return t(`status.${status}` as any, { defaultValue: status || t('status.unknown') })
  }

  // Get stakeholder type name
  const getStakeholderTypeName = (stakeholderTypeUid: string) => {
    const type = stakeholderTypes.find(st => st.uid === stakeholderTypeUid)
    if (!type) return t('detail.stakeholder_type_unknown')
    return getLocalizedName(type.names, locale) || type.code
  }

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/contracts` },
    {
      title: contract ? getContractTitle(contract) : t('detail.breadcrumb_details'),
      path: `/${org}/contracts/${contractUid}`
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [org, contract, contractUid, t])

  useBreadcrumbs(breadcrumbs)

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="flex items-center justify-center h-64">
          <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  if (error || !contract) {
    return (
      <div className="container mx-auto py-8 max-w-6xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">{t('detail.error_title')}</h1>
          <p className="text-muted-foreground">
            {(error as any)?.title || (error as any)?.message || t('detail.error_not_found')}
          </p>
          <a href={`/${org}/contracts`}>
            <Button className="mt-4">
              <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
              {t('detail.back_to_contracts')}
            </Button>
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              {editingTitle ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => { blurGuard.current = false; setTitleValue(e.target.value) }}
                    onKeyDown={handleTitleKeyDown}
                    onBlur={blurCommit(commitTitle)}
                    autoFocus
                    disabled={updateMutation.isPending}
                    aria-label={t('detail.field_title')}
                    className={`text-xl sm:text-2xl font-bold text-foreground bg-background border rounded px-2 py-0.5 flex-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-ring ${titleError ? 'border-destructive' : 'border-input'}`}
                    data-testid="contract-title-input"
                  />
                  {titleError && (
                    <span className="text-xs text-destructive" data-testid="contract-title-error">{titleError}</span>
                  )}
                </div>
              ) : (
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                  <EditableValue
                    canEdit={canWrite}
                    onEdit={startTitleEdit}
                    title={t('detail.edit_title')}
                    placeholder={t('detail.click_to_edit')}
                    data-testid="contract-title-display"
                  >
                    {getContractTitle(contract)}
                  </EditableValue>
                </h1>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
              <Badge variant={getStatusVariant((contract as any).status || 'draft')}>
                {getStatusLabel((contract as any).status || 'draft')}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {t('detail.type_label', { type: getContractTypeName(contract) })}
              </span>
              {(contract as any).code && (
                <Badge variant="secondary" className="text-sm">
                  {t('detail.ref_label', { code: (contract as any).code })}
                </Badge>
              )}
              {/* Avenant (amendment) chain: badge when this contract is itself an
                  amendment, and a link back to the contract it supersedes. */}
              {Array.isArray((contract as any).flags) && (contract as any).flags.includes('amendment') && (
                <Badge variant="outline" className="text-sm gap-1" data-testid="contract-amendment-badge">
                  <AppIcon path={mdiSourceBranch} size="xs" />
                  {t('detail.amendment_badge')}
                </Badge>
              )}
              {/* Tri-state PII badge + verdict menu (spec 2026-08-13-02 §8). */}
              <PIIBadge
                flags={(contract as { flags?: string[] }).flags}
                onSetStatus={setPIIStatus}
                testId="contract-pii-badge"
              />
              {/* "Individual" — the contract has at least one natural-person or
                  party-group party. Computed server-side from the stakeholders
                  and deliberately independent of the PII tokens. */}
              {(contract as { with_individual?: boolean }).with_individual && (
                <Badge
                  variant="outline"
                  className="text-sm gap-1"
                  title={t('pii.individual.hint', { ns: 'common' })}
                  data-testid="contract-individual-badge"
                >
                  <AppIcon path={mdiAccount} size="xs" />
                  {t('pii.individual.badge', { ns: 'common' })}
                </Badge>
              )}
              {qualityIssues.length > 0 && (
                <Badge
                  variant="outline"
                  className="text-sm gap-1 text-warning border-warning/40"
                  title={qualityIssues
                    .map((i) => subtypeLabel(subtypeCatalog[i.subtype || ''], locale) || i.title)
                    .join(' · ')}
                  data-testid="contract-conformity-badge"
                >
                  <AppIcon path={mdiShieldAlertOutline} size="xs" />
                  {t('list.conformity_flagged', { count: qualityIssues.length })}
                </Badge>
              )}
            </div>
            {/* Avenant chain as a compact period timeline (predecessor →
                current → successor). Renders nothing for a standalone contract. */}
            {contract && (
              <ContractChainStrip org={org || 'demo'} current={contract as any} />
            )}
            {contract.uid && (
              <div className="flex flex-wrap items-center gap-1 mt-2">
                <EntityTagsSection
                  org={org || 'demo'}
                  entityType="contract"
                  entityUid={contract.uid}
                  className="flex flex-wrap items-center gap-1"
                  collapse={{ below: 'md', max: 2 }}
                  editable={canWrite}
                />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Direct link to the contract's main document (#1787): the one
                the clauses were extracted from, otherwise buried in the
                Documents card's folder tree. */}
            {(contract as any).source_document_uid && (
              <Link
                to="/$org/documents/$docUid"
                params={{ org: org || 'demo', docUid: (contract as any).source_document_uid }}
                data-testid="contract-open-source-document"
              >
                <Button variant="outline" size="sm" className="gap-1" title={t('detail.open_source_document_hint')}>
                  <AppIcon path={SECTION_ICONS.documents} size="sm" />
                  {t('detail.open_source_document')}
                </Button>
              </Link>
            )}
            {contract.uid && (
              <Link
                to="/$org/graph"
                params={{ org: org || 'demo' }}
                search={{ focus: `ctr@${contract.uid}`, expanded: undefined }}
              >
                <Button
                  variant="outline"
                  size="icon"
                  data-testid="view-in-graph-btn"
                  aria-label={t('graph:action.view_in_graph')}
                  title={t('graph:action.view_in_graph')}
                >
                  <AppIcon path={mdiGraphOutline} size="sm" />
                </Button>
              </Link>
            )}
            <Button
              variant="destructive"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleteMutation.isPending}
              data-testid="delete-contract"
              aria-label={t('detail.delete')}
              title={t('detail.delete')}
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </div>

        {/* Detail body — tabbed Details / Clauses. */}
        <Tabs value={activeTab || 'details'} onValueChange={handleTabChange}>
          <TabsList className="mb-6" data-testid="contract-tabs">
            <TabsTrigger value="details" data-testid="contract-tab-details">
              <AppIcon path={SECTION_ICONS.documents} size="sm" className="mr-2" />
              {t('detail.tab_details')}
            </TabsTrigger>
            <TabsTrigger value="clauses" data-testid="contract-tab-clauses">
              <AppIcon path={mdiScriptTextOutline} size="sm" className="mr-2" />
              {t('detail.tab_clauses')}
            </TabsTrigger>
            {(contract as any)?.is_lease && (
              <TabsTrigger value="rent-schedule" data-testid="contract-tab-rent-schedule">
                <AppIcon path={mdiCalendar} size="sm" className="mr-2" />
                {t('lease.rent_schedule.tab')}
              </TabsTrigger>
            )}
            <TabsTrigger value="improvement" data-testid="contract-tab-improvement">
              <AppIcon path={mdiScaleBalance} size="sm" className="mr-2" />
              {t('detail.tab_improvement')}
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="contract-tab-reports">
              <AppIcon path={SECTION_ICONS.documents} size="sm" className="mr-2" />
              {t('detail.tab_reports')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
        {contract?.uid && (
          <div className="mb-6">
            <CommercialLeaseCard org={org || 'demo'} contractUid={contract.uid} onOpenTab={handleTabChange} />
          </div>
        )}
        {contract?.uid && (
          <div className="mb-6">
            <ContractExecutionCard
              org={org || 'demo'}
              contractUid={contract.uid}
              hasSourceDocument={!!(contract as any).source_document_uid}
              onOpenTab={handleTabChange}
            />
          </div>
        )}
        {contract?.uid && (contract as any).is_lease && (
          <div className="mb-6">
            <LeaseTermsCard org={org || 'demo'} contract={contract as any} onOpenTab={handleTabChange} />
          </div>
        )}
        {contract?.uid && (contract as any).is_lease && (
          <div className="mb-6">
            <ContractLeaseExitCard org={org || 'demo'} contract={contract as any} />
          </div>
        )}
        {contract?.uid && (contract as any).is_lease && (
          <div className="mb-6">
            <GuaranteesCard org={org || 'demo'} contractUid={contract.uid} />
          </div>
        )}
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contract Details - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* What the general assembly authorizes (spec 2026-10-05-02). The
                minutes and their resolutions are the authorization, not subject to it. */}
            {contract.uid && !['RESOLUTION', 'ASSEMBLY_MINUTES'].includes((contract as any).contract_type?.code) && (
              <ContractCoverageCard
                org={org || 'demo'}
                contractUid={contract.uid}
                authorizedByUid={(contract as any).authorized_by_contract_uid}
                canWrite={canWrite}
              />
            )}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AppIcon path={SECTION_ICONS.documents} size="md" />
                  {t('detail.section_details')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Contract Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_status')}</h3>
                    {editingStatus ? (
                      <div className="space-y-2">
                        <Select
                          defaultOpen
                          value={statusValue}
                          onValueChange={commitStatus}
                          onOpenChange={(open) => {
                            // Closed without a pick (Escape, click away): back to text.
                            if (!open && !statusPicked.current) cancelStatusEdit()
                          }}
                          disabled={updateMutation.isPending}
                        >
                          <SelectTrigger className="w-full" data-testid="contract-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTRACT_STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value} data-testid={`contract-status-option-${option.value}`}>
                                <span className="flex items-center gap-2">
                                  <span className={`h-2 w-2 rounded-full ${option.dot}`} />
                                  {t(`status.${option.value}` as any)}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {statusError && (
                          <Alert variant="destructive" data-testid="contract-status-error">
                            <AlertDescription>{statusError}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ) : (
                      <EditableValue
                        canEdit={canWrite}
                        onEdit={startStatusEdit}
                        title={t('detail.click_to_edit')}
                        placeholder={t('detail.click_to_edit')}
                        className="inline-block py-0.5"
                        data-testid="contract-status-display"
                      >
                        <Badge variant={getStatusVariant((contract as any).status || 'draft')} className="text-sm">
                          {getStatusLabel((contract as any).status || 'draft')}
                        </Badge>
                      </EditableValue>
                    )}
                  </div>

                  {/* Contract Type */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_contract_type')}</h3>
                    {editingType ? (
                      <div className="space-y-2">
                        <select
                          value={typeValue}
                          onChange={(e) => commitType(e.target.value)}
                          onBlur={() => cancelTypeEdit()}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault()
                              cancelTypeEdit()
                            }
                          }}
                          autoFocus
                          disabled={updateMutation.isPending}
                          className="mt-1 w-full px-3 py-2 border rounded-md"
                          data-testid="contract-type-select"
                        >
                          <option value="">{t('detail.field_contract_type_none')}</option>
                          {/* A family and its sub-types are both pickable; optgroup
                              is the native equivalent of the indented list the
                              creation form shows. */}
                          {contractTypeGroups.map(({ family, children }) =>
                            children.length === 0 ? (
                              <option key={family.uid} value={family.uid}>
                                {contractTypeLabel(family, locale)}
                              </option>
                            ) : (
                              <optgroup key={family.uid} label={contractTypeLabel(family, locale)}>
                                <option value={family.uid}>{contractTypeLabel(family, locale)}</option>
                                {children.map((child) => (
                                  <option key={child.uid} value={child.uid}>
                                    {contractTypeLabel(child, locale)}
                                  </option>
                                ))}
                              </optgroup>
                            )
                          )}
                        </select>
                        {typeError && (
                          <Alert variant="destructive" data-testid="contract-type-error">
                            <AlertDescription>{typeError}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ) : (
                      <EditableValue
                        canEdit={canWrite}
                        onEdit={startTypeEdit}
                        title={t('detail.click_to_edit')}
                        placeholder={t('detail.click_to_edit')}
                        className="text-muted-foreground"
                        data-testid="contract-type-display"
                      >
                        {getContractTypeName(contract)}
                      </EditableValue>
                    )}
                  </div>

                  {/* Reference */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{t('detail.field_reference')}</h3>
                    {editingReference ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={referenceValue}
                          onChange={(e) => { blurGuard.current = false; setReferenceValue(e.target.value) }}
                          onKeyDown={handleReferenceKeyDown}
                          onBlur={blurCommit(commitReference)}
                          autoFocus
                          disabled={updateMutation.isPending}
                          className="mt-1 w-full px-3 py-2 border rounded-md"
                          placeholder={t('detail.field_reference_placeholder')}
                          data-testid="contract-reference-input"
                        />
                        {referenceError && (
                          <Alert variant="destructive" data-testid="contract-reference-error">
                            <AlertDescription>{referenceError}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ) : (
                      <EditableValue
                        canEdit={canWrite}
                        onEdit={startReferenceEdit}
                        title={t('detail.click_to_edit')}
                        placeholder={t('detail.click_to_edit')}
                        data-testid="contract-reference-display"
                      >
                        {(contract as any).code ? (
                          <Badge variant="secondary" className="text-sm">{(contract as any).code}</Badge>
                        ) : null}
                      </EditableValue>
                    )}
                  </div>

                  {/* Start Date */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <AppIcon path={mdiCalendar} size="sm" />
                      {t('detail.field_start_date')}
                    </h3>
                    <NullableDateField
                      value={(contract as any).start_date ?? null}
                      onSave={(next) => saveDateField('start_date', next)}
                      disabled={!canWrite || updateMutation.isPending}
                      testIdPrefix="contract-start-date"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                      <AppIcon path={mdiCalendar} size="sm" />
                      {t('detail.field_end_date')}
                    </h3>
                    <NullableDateField
                      value={(contract as any).end_date ?? null}
                      onSave={(next) => saveDateField('end_date', next)}
                      disabled={!canWrite || updateMutation.isPending}
                      testIdPrefix="contract-end-date"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-foreground mb-2">{t('detail.field_description')}</h3>
                  {editingDescriptionField ? (
                    <div className="space-y-2">
                      <Textarea
                        value={descriptionValue}
                        onChange={(e) => { blurGuard.current = false; setDescriptionValue(e.target.value) }}
                        onKeyDown={handleDescriptionKeyDown}
                        onBlur={blurCommit(commitDescription)}
                        rows={4}
                        autoFocus
                        disabled={updateMutation.isPending}
                        placeholder={t('detail.field_description_placeholder')}
                        aria-label={t('detail.field_description')}
                        data-testid="contract-description-input"
                      />
                      <p className="text-xs text-muted-foreground">{t('detail.description_edit_hint')}</p>
                      {descriptionError && (
                        <Alert variant="destructive" data-testid="contract-description-error">
                          <AlertDescription>{descriptionError}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <EditableValue
                      as="p"
                      canEdit={canWrite}
                      onEdit={startDescriptionEdit}
                      title={t('detail.click_to_edit')}
                      placeholder={t('detail.click_to_edit')}
                      className="block text-muted-foreground leading-relaxed py-0.5"
                      data-testid="contract-description-display"
                    >
                      {(contract as any).description || (canWrite ? null : t('detail.field_description_empty'))}
                    </EditableValue>
                  )}
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('detail.field_created')}</p>
                    <p className="text-sm">
                      {(contract as any).created_at ? new Date((contract as any).created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('detail.field_updated')}</p>
                    <p className="text-sm">
                      {(contract as any).updated_at ? new Date((contract as any).updated_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stakeholders */}
            <Card
              onKeyDown={(e) => {
                if (e.key === 'Escape' && editingStakeholders) {
                  e.preventDefault()
                  doneStakeholdersEdit()
                }
              }}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AppIcon path={mdiAccountGroup} size="md" />
                      {t('detail.stakeholders_title')}
                    </CardTitle>
                    <CardDescription>
                      {t(`detail.stakeholders_count_${enrichedStakeholders.length === 1 ? 'one' : 'other'}` as any, { count: enrichedStakeholders.length })}
                    </CardDescription>
                  </div>
                  <Can perm="contracts.write">
                    {editingStakeholders ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={doneStakeholdersEdit}
                        aria-label={t('detail.done')}
                        title={t('detail.done')}
                        data-testid="contract-stakeholders-done"
                      >
                        <AppIcon path={mdiCheck} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('detail.done')}</span>
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={startStakeholdersEdit}
                        aria-label={t('detail.edit_stakeholders')}
                        title={t('detail.edit_stakeholders')}
                        data-testid="contract-stakeholders-edit"
                      >
                        <AppIcon path={mdiPencil} size="sm" />
                      </Button>
                    )}
                  </Can>
                </div>
              </CardHeader>
              <CardContent>
                {editingStakeholders && (
                  <Can perm="contracts.write">
                    <div className="mb-3 space-y-2 rounded-lg border border-border p-3" data-testid="contract-stakeholder-add-row">
                      {stakeholderConflicts && (
                        <GuaranteeConflictConfirmDialog conflicts={stakeholderConflicts}
                          onCancel={() => setStakeholderConflicts(null)} onConfirm={() => confirmAddStakeholder(true)} />
                      )}
                      <Select value={newStakeholderTypeUid} onValueChange={setNewStakeholderTypeUid}>
                        <SelectTrigger data-testid="contract-stakeholder-type-select">
                          <SelectValue placeholder={t('create.stakeholder_type_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {stakeholderTypes.map((stype) => (
                            <SelectItem key={stype.uid} value={stype.uid}>
                              {getStakeholderTypeName(stype.uid)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <UserSelect
                        org={org || 'demo'}
                        value={newStakeholderUserUid}
                        onValueChange={setNewStakeholderUserUid}
                        placeholder={t('create.user_placeholder')}
                        data-testid="contract-stakeholder-user-select"
                      />
                      {stakeholderAlreadyLinked && (
                        <p className="text-xs text-destructive" data-testid="contract-stakeholder-duplicate">
                          {t('detail.stakeholder_already_linked')}
                        </p>
                      )}
                      {stakeholderAddError && (
                        <Alert variant="destructive" data-testid="contract-stakeholder-add-error">
                          <AlertDescription>{stakeholderAddError}</AlertDescription>
                        </Alert>
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => confirmAddStakeholder()}
                          disabled={!newStakeholderTypeUid || !newStakeholderUserUid || stakeholderAlreadyLinked || addStakeholderMutation.isPending}
                          data-testid="contract-stakeholder-add-confirm"
                        >
                          {t('detail.save')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={resetStakeholderAddRow}
                          disabled={addStakeholderMutation.isPending}
                          data-testid="contract-stakeholder-add-cancel"
                        >
                          {t('detail.cancel')}
                        </Button>
                      </div>
                    </div>
                  </Can>
                )}
                {enrichedStakeholders.length === 0 ? (
                  <EmptyState variant="inline" title={t('detail.stakeholders_empty')} />
                ) : (
                  <div className="space-y-3">
                    {enrichedStakeholders.map((stakeholder) => (
                      <div key={stakeholder.uid} className="p-3 rounded-lg border border-border bg-card/50" data-testid={`contract-stakeholder-row-${stakeholder.uid}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {(stakeholder as any).user ? (
                              <a
                                href={`/${org}/users/${(stakeholder as any).user.uid}`}
                                className="font-medium text-primary hover:text-primary/80 hover:underline truncate block"
                              >
                                {(stakeholder as any).user.name || (stakeholder as any).user.email || t('detail.user_unknown')}
                              </a>
                            ) : (
                              <p className="font-medium truncate">
                                {(stakeholder as any).name || t('detail.stakeholder_unknown')}
                              </p>
                            )}

                            {/* Stakeholder Type */}
                            <p className="text-xs text-muted-foreground mt-1">
                              {getStakeholderTypeName(stakeholder.stakeholder_type_uid)}
                            </p>

                            {/* Email */}
                            {(stakeholder as any).user?.email && (
                              <p className="text-xs text-muted-foreground truncate">
                                {(stakeholder as any).user.email}
                              </p>
                            )}

                            {/* Role if available */}
                            {(stakeholder as any).role && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {(stakeholder as any).role}
                              </p>
                            )}
                          </div>
                          {editingStakeholders && (
                            <Can perm="contracts.write">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeStakeholder(stakeholder.uid)}
                                disabled={removeStakeholderMutation.isPending}
                                aria-label={t('detail.remove_stakeholder')}
                                title={t('detail.remove_stakeholder')}
                                data-testid={`contract-stakeholder-remove-${stakeholder.uid}`}
                              >
                                <AppIcon path={mdiClose} size="sm" />
                              </Button>
                            </Can>
                          )}
                        </div>
                        {stakeholderRemoveErrors[stakeholder.uid] && (
                          <Alert variant="destructive" className="mt-2" data-testid={`contract-stakeholder-remove-error-${stakeholder.uid}`}>
                            <AlertDescription>{stakeholderRemoveErrors[stakeholder.uid]}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assets */}
            <Card
              onKeyDown={(e) => {
                if (e.key === 'Escape' && editingAssets) {
                  e.preventDefault()
                  doneAssetsEdit()
                }
              }}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AppIcon path={SECTION_ICONS.assets} size="md" />
                      {t('detail.assets_title')}
                    </CardTitle>
                    <CardDescription>
                      {t(`detail.assets_count_${contractAssets.length === 1 ? 'one' : 'other'}` as any, { count: contractAssets.length })}
                    </CardDescription>
                  </div>
                  <Can perm="contracts.write">
                    {editingAssets ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={doneAssetsEdit}
                        aria-label={t('detail.done')}
                        title={t('detail.done')}
                        data-testid="contract-assets-done"
                      >
                        <AppIcon path={mdiCheck} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('detail.done')}</span>
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={startAssetsEdit}
                        aria-label={t('detail.edit_assets')}
                        title={t('detail.edit_assets')}
                        data-testid="contract-assets-edit"
                      >
                        <AppIcon path={mdiPencil} size="sm" />
                      </Button>
                    )}
                  </Can>
                </div>
              </CardHeader>
              <CardContent>
                {editingAssets && (
                  <Can perm="contracts.write">
                    <div className="mb-3 space-y-2 rounded-lg border border-border p-3" data-testid="contract-asset-add-row">
                      <AssetPicker
                        org={org || 'demo'}
                        value={newAssetUid}
                        valueLabel={newAssetLabel}
                        onChange={(uid, name) => { setNewAssetUid(uid); setNewAssetLabel(name) }}
                        testId="contract-asset-picker"
                      />
                      {assetAlreadyLinked && (
                        <p className="text-xs text-destructive" data-testid="contract-asset-duplicate">
                          {t('detail.asset_already_linked')}
                        </p>
                      )}
                      {assetAddError && (
                        <Alert variant="destructive" data-testid="contract-asset-add-error">
                          <AlertDescription>{assetAddError}</AlertDescription>
                        </Alert>
                      )}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={confirmAddAsset}
                          disabled={!newAssetUid || assetAlreadyLinked || addAssetMutation.isPending}
                          data-testid="contract-asset-add-confirm"
                        >
                          {t('detail.save')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={resetAssetAddRow}
                          disabled={addAssetMutation.isPending}
                          data-testid="contract-asset-add-cancel"
                        >
                          {t('detail.cancel')}
                        </Button>
                      </div>
                    </div>
                  </Can>
                )}
                {(contract as any)?.is_lease && contractAssets.length > 1 && !contractAssets.some((a) => (a as any).is_primary) && (
                  <PrimaryMissingBanner />
                )}
                {contractAssets.length === 0 ? (
                  <EmptyState variant="inline" title={t('detail.assets_empty')} />
                ) : (
                  <div className="space-y-3">
                    {contractAssets.map((contractAsset) => (
                      <div key={contractAsset.uid} className="p-3 rounded-lg border border-border bg-card/50" data-testid={`contract-asset-row-${contractAsset.uid}`}>
                        <div className="flex items-start gap-3">
                          <AppIcon
                            path={contractAsset.asset?.archived_at ? mdiArchiveOutline : SECTION_ICONS.assets}
                            size="sm"
                            className={`mt-0.5 flex-shrink-0 ${contractAsset.asset?.archived_at ? 'text-muted-foreground' : ''}`}
                            data-testid={contractAsset.asset?.archived_at ? 'contract-asset-archived-icon' : undefined}
                          />
                          <div className="flex-1 min-w-0">
                            {/* Asset Link */}
                            <p className="font-medium text-primary hover:text-primary/80 hover:underline truncate">
                              <a href={`/${org}/assets/${contractAsset.asset_uid}`}>
                                <AssetName
                                  name={(contractAsset.asset as any)?.name || t('detail.asset_unknown')}
                                  archivedAt={contractAsset.asset?.archived_at}
                                />
                              </a>
                            </p>

                            {/* Asset Type */}
                            {(contractAsset.asset as any)?.asset_type?.name && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {t('detail.asset_type_prefix', { type: (contractAsset.asset as any).asset_type.name })}
                              </p>
                            )}

                            {/* Asset Address */}
                            {(contractAsset.asset as any)?.address && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <AppIcon path={mdiMapMarker} size="xs" />
                                {(contractAsset.asset as any).address}
                              </p>
                            )}

                            {/* Contract-specific asset description */}
                            {contractAsset.description && (
                              <p className="text-xs text-muted-foreground mt-2 italic">
                                {contractAsset.description}
                              </p>
                            )}
                          </div>
                          {(contract as any)?.is_lease && contract?.uid && (
                            <PrimaryAssetRadio org={org || 'demo'} contractUid={contract.uid} assetUid={contractAsset.asset_uid}
                              isPrimary={!!(contractAsset as any).is_primary} />
                          )}
                          {editingAssets && (
                            <Can perm="contracts.write">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeAsset(contractAsset.asset_uid)}
                                disabled={removeAssetMutation.isPending}
                                aria-label={t('detail.remove_asset')}
                                title={t('detail.remove_asset')}
                                data-testid={`contract-asset-remove-${contractAsset.uid}`}
                              >
                                <AppIcon path={mdiClose} size="sm" />
                              </Button>
                            </Can>
                          )}
                        </div>
                        {assetRemoveErrors[contractAsset.asset_uid] && (
                          <Alert variant="destructive" className="mt-2" data-testid={`contract-asset-remove-error-${contractAsset.uid}`}>
                            <AlertDescription>{assetRemoveErrors[contractAsset.asset_uid]}</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {contract?.uid && (
          <ContractRelatedExpenses org={org || 'demo'} contractUid={contract.uid} />
        )}

        {/* Issues */}
        {contract?.uid && (
          <Card data-testid="contract-issues-section" className="mt-6">
            <CardHeader>
              <CardTitle>{t('detail.issues')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityIssuesList targetType="contract" targetEntityUid={contract.uid} />
            </CardContent>
          </Card>
        )}

        {contract?.uid && (
          <ContractDocuments
            org={org || 'demo'}
            contractUid={contract.uid}
            flags={(contract as any).flags}
            sourceDocumentUid={(contract as any).source_document_uid || null}
          />
        )}

        {/* Signature requests carried by this contract (spec 2026-10-05-05) */}
        {contract?.uid && (
          <SignatureRequestsPanel
            org={org || 'demo'}
            filter={{ subject_type: 'contract', subject_uid: contract.uid }}
            testId="contract-signature-requests"
          />
        )}
          </TabsContent>

          <TabsContent value="clauses">
            {archivedContractAssets.length > 0 && (
              <Alert className="mb-4" data-testid="contract-archived-asset-notice">
                <AlertDescription>
                  {t('clauses.archived_asset_notice', {
                    names: archivedContractAssets.map((a: any) => a.asset?.name).filter(Boolean).join(', '),
                    count: archivedContractAssets.length,
                  })}
                </AlertDescription>
              </Alert>
            )}
            <ContractClausesTab
              org={org || 'demo'}
              contractUid={contractUid || ''}
              contractTypeUid={(contract as any).contract_type?.uid || (contract as any).contract_type_uid || ''}
              sourceDocumentUid={(contract as any).source_document_uid || undefined}
              assets={contractAssets}
            />
          </TabsContent>

          {(contract as any)?.is_lease && contract?.uid && (
            <TabsContent value="rent-schedule">
              <RentScheduleTab org={org || 'demo'} contractUid={contract.uid} assets={contractAssets as any} />
            </TabsContent>
          )}

          <TabsContent value="improvement">
            <ContractImprovementTab
              org={org || 'demo'}
              contractUid={contractUid || ''}
              contractTypeUid={(contract as any).contract_type?.uid || (contract as any).contract_type_uid || ''}
              hasType={!!((contract as any).contract_type?.uid || (contract as any).contract_type_uid)}
            />
          </TabsContent>

          <TabsContent value="reports">
            <ContractReportsTab
              org={org || 'demo'}
              contractUid={contractUid || ''}
              assetUids={contractAssets.map((a: any) => a.asset_uid || a.asset?.uid).filter(Boolean)}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete confirmation — AlertDialog (never native confirm()). */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="delete-contract-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.delete_confirm_description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-contract-cancel">{t('detail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="delete-contract-confirm"
            >
              {t('detail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}